import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Languages, Heart, ChevronDown } from 'lucide-react';
import { Button } from '../ui/Button';
import { cn } from '../../lib/utils';
import { useLanguage } from '../../context/LanguageContext';
import { Language } from '../../lib/translations';
export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const {
    language,
    setLanguage,
    t,
    isRTL
  } = useLanguage();
  const location = useLocation();
  const langDropdownRef = useRef<HTMLDivElement>(null);
  const navigation = [{
    name: t('nav.home'),
    href: '/'
  }, {
    name: t('nav.about'),
    href: '/about'
  }, {
    name: t('nav.membership'),
    href: '/membership'
  }, {
    name: t('nav.events'),
    href: '/events'
  }, {
    name: t('nav.charity'),
    href: '/charity'
  }, {
    name: t('nav.team'),
    href: '/team'
  }, {
    name: t('nav.contact'),
    href: '/contact'
  }];
  const languages: {
    code: Language;
    label: string;
  }[] = [{
    code: 'en',
    label: 'English'
  }, {
    code: 'ps',
    label: 'Pashto'
  }, {
    code: 'ur',
    label: 'Urdu'
  }];
  const currentLanguageLabel = languages.find(l => l.code === language)?.label || 'English';
  const isActive = (path: string) => {
    if (path === '/' && location.pathname !== '/') return false;
    return location.pathname.startsWith(path);
  };
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target as Node)) {
        setIsLangDropdownOpen(false);
      }
    }
    if (isLangDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isLangDropdownOpen]);
  const handleLanguageChange = (lang: Language) => {
    setLanguage(lang);
    setIsLangDropdownOpen(false);
  };
  return <header className="sticky top-0 z-50 w-full border-b border-gray-100 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60" data-id="element-64">
      <div className="container mx-auto px-4 md:px-6" data-id="element-65">
        <div className="flex h-20 items-center justify-between" data-id="element-66">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-id="element-67">
            <img src="https://cdn.magicpatterns.com/uploads/stkewdFoDLsveAsLMC6296/favi__logo.png" alt="Khyber Medical Association UK Logo" className="h-14 w-14 object-contain" data-id="element-68" />
            <div className="flex flex-col" data-id="element-69">
              <span className="text-lg font-serif font-bold leading-none text-primary-900" data-id="element-70">
                KMA UK
              </span>
              <span className="text-xs text-gray-500" data-id="element-71">
                Khyber Medical Association
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center space-x-6" data-id="element-72">
            {navigation.map(item => <Link key={item.href} to={item.href} className={cn('text-sm font-medium transition-colors hover:text-primary-700', isActive(item.href) ? 'text-primary-700 font-semibold' : 'text-gray-600')} data-id="element-73">
                {item.name}
              </Link>)}

            {/* Language Selector - Desktop */}
            <div className="relative ml-2" ref={langDropdownRef} data-id="element-74">
              <button onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 hover:text-primary-700 hover:bg-gray-50 rounded-lg transition-colors" data-id="element-75">
                <Languages className="h-5 w-5 text-blue-600" data-id="element-76" />
                <span data-id="element-77">{t('nav.language')}</span>
                <ChevronDown className={cn('h-4 w-4 transition-transform', isLangDropdownOpen && 'rotate-180')} data-id="element-78" />
              </button>
              {isLangDropdownOpen && <div className={cn('absolute mt-2 w-40 bg-white rounded-lg shadow-lg py-2 border border-gray-100 z-50', isRTL ? 'left-0' : 'right-0')} data-id="element-79">
                  {languages.map(lang => <button key={lang.code} onClick={() => handleLanguageChange(lang.code)} className={cn('block w-full text-left px-4 py-2 text-sm transition-colors', language === lang.code ? 'bg-primary-50 text-primary-700 font-semibold' : 'text-gray-700 hover:bg-gray-50')} data-id="element-80">
                      {lang.label}
                    </button>)}
                </div>}
            </div>

            <div className="flex items-center gap-3 ml-4" data-id="element-81">
              <Link to="/membership" data-id="element-82">
                <Button size="sm" variant="primary" data-id="element-83">
                  {t('nav.joinMembership')}
                </Button>
              </Link>
              <a href="https://donate.stripe.com/test_kmauk_placeholder" target="_blank" rel="noopener noreferrer" data-id="element-84">
                <Button size="sm" variant="secondary" data-id="element-85">
                  <Heart className={cn('h-4 w-4', isRTL ? 'ml-2' : 'mr-2')} data-id="element-86" />
                  {t('nav.donate')}
                </Button>
              </a>
            </div>
          </nav>

          {/* Mobile Menu Button */}
          <button className="xl:hidden p-2 text-gray-600" onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label="Toggle menu" data-id="element-87">
            {isMenuOpen ? <X className="h-6 w-6" data-id="element-88" /> : <Menu className="h-6 w-6" data-id="element-89" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && <div className="xl:hidden border-t border-gray-100 bg-white max-h-[calc(100vh-5rem)] overflow-y-auto" data-id="element-90">
          <div className="container mx-auto px-4 py-4 space-y-4" data-id="element-91">
            {navigation.map(item => <Link key={item.href} to={item.href} className={cn('block text-base font-medium py-2', isActive(item.href) ? 'text-primary-700' : 'text-gray-600')} onClick={() => setIsMenuOpen(false)} data-id="element-92">
                {item.name}
              </Link>)}

            <div className="py-2 border-t border-gray-100" data-id="element-93">
              <div className="flex items-center gap-2 mb-3" data-id="element-94">
                <Languages className="h-5 w-5 text-blue-600" data-id="element-95" />
                <p className="text-sm font-semibold text-gray-700" data-id="element-96">
                  {t('nav.language')}
                </p>
              </div>
              <div className="flex gap-3" data-id="element-97">
                {languages.map(lang => <button key={lang.code} onClick={() => {
              setLanguage(lang.code);
              setIsMenuOpen(false);
            }} className={cn('text-sm px-4 py-2 rounded-lg border font-medium transition-colors', language === lang.code ? 'bg-primary-600 border-primary-600 text-white' : 'border-gray-200 text-gray-700 hover:border-primary-300 hover:bg-primary-50')} data-id="element-98">
                    {lang.label}
                  </button>)}
              </div>
            </div>

            <div className="pt-4 space-y-3" data-id="element-99">
              <Link to="/membership" onClick={() => setIsMenuOpen(false)} data-id="element-100">
                <Button className="w-full" data-id="element-101">{t('nav.joinMembership')}</Button>
              </Link>
              <a href="https://donate.stripe.com/test_kmauk_placeholder" target="_blank" rel="noopener noreferrer" className="block" data-id="element-102">
                <Button variant="secondary" className="w-full" data-id="element-103">
                  <Heart className={cn('h-4 w-4', isRTL ? 'ml-2' : 'mr-2')} data-id="element-104" />
                  {t('nav.donate')}
                </Button>
              </a>
            </div>
          </div>
        </div>}
    </header>;
}